import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MapPin, DollarSign, Route, ArrowRight, FileText } from 'lucide-react';
import MapView from '@/components/MapView';
import { getLoadStatusClasses } from '@/lib/statusColors';

const milesToKm = (miles) => Math.round((miles || 0) * 1.60934);
const ratePerKm = (rate, miles) => ((rate || 0) / ((miles || 1) * 1.60934)).toFixed(2);

export default function DriverHistory() {
  const { token, user } = useAuth();
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoad, setDetailLoad] = useState(null);
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  const INVOICE_STATUS_COLORS = {
    paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
    sent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800',
    draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400 border-gray-200 dark:border-gray-700',
  };

  // Fetch invoice when detail dialog opens
  useEffect(() => {
    if (!detailLoad) {
      setDetailInvoice(null);
      return;
    }
    setInvoiceLoading(true);
    api.get('/invoices', { params: { load_id: detailLoad.load_id } })
      .then(r => {
        const inv = Array.isArray(r.data) ? r.data[0] : r.data;
        setDetailInvoice(inv || null);
      })
      .catch(() => setDetailInvoice(null))
      .finally(() => setInvoiceLoading(false));
  }, [detailLoad]);

  useEffect(() => {
    api.get('/loads')
      .then(r => {
        const completed = r.data.filter(l => ['completed', 'invoiced'].includes(l.status) && l.driver_id === user?.user_id);
        setLoads(completed);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token, user]);

  const totalEarnings = loads.reduce((sum, l) => sum + (l.rate || 0), 0);
  const totalKm = loads.reduce((sum, l) => sum + milesToKm(l.distance_miles), 0);

  if (loading) return <div className="space-y-4">{Array(3).fill(0).map((_, i) => <div key={i} className="h-20 bg-muted rounded-2xl animate-pulse" />)}</div>;

  return (
    <div className="space-y-4" data-testid="driver-history">
      <h1 className="text-2xl font-bold tracking-tight">History</h1>

      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-emerald-50 border-emerald-200 dark:bg-card dark:border-border rounded-2xl">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">${totalEarnings.toLocaleString()}</p>
            <p className="text-[10px] text-emerald-500 dark:text-muted-foreground font-bold tracking-wider">EARNED</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200 dark:bg-card dark:border-border rounded-2xl">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{loads.length}</p>
            <p className="text-[10px] text-blue-500 dark:text-muted-foreground font-bold tracking-wider">LOADS</p>
          </CardContent>
        </Card>
        <Card className="bg-cyan-50 border-cyan-200 dark:bg-card dark:border-border rounded-2xl">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-cyan-600 dark:text-cyan-400">{totalKm.toLocaleString()}</p>
            <p className="text-[10px] text-cyan-500 dark:text-muted-foreground font-bold tracking-wider">KM DRIVEN</p>
          </CardContent>
        </Card>
      </div>

      {loads.length === 0 ? (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-8 text-center text-muted-foreground">No completed loads yet</CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="history-list">
          {loads.map(load => (
            <Card key={load.load_id}
              className="rounded-2xl border-border cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setDetailLoad(load)} data-testid={`history-load-${load.load_id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold">{load.cargo_description}</p>
                    <div className="flex items-center text-xs text-muted-foreground mt-1">
                      <MapPin className="w-3 h-3 mr-1 shrink-0" />
                      <span className="truncate">{load.pickup_name || load.pickup_address?.split(',')[0]}</span>
                      <ArrowRight className="w-3 h-3 mx-1 shrink-0" />
                      <span className="truncate">{load.delivery_name || load.delivery_address?.split(',')[0]}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span>{milesToKm(load.distance_miles)} km</span>
                      <span>{new Date(load.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="text-right ml-3">
                    <p className="text-lg font-bold text-emerald-600">${load.rate?.toLocaleString()}</p>
                    <Badge className={`text-[10px] border ${getLoadStatusClasses(load.status)}`}>
                      {load.status?.toUpperCase()}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detailLoad} onOpenChange={() => setDetailLoad(null)}>
        <DialogContent className="bg-background max-w-md">
          <DialogHeader><DialogTitle>Load Details</DialogTitle></DialogHeader>
          {detailLoad && (
            <div className="space-y-4" data-testid="history-detail">
              <div>
                <h3 className="font-semibold text-base">{detailLoad.cargo_description}</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge className={`text-[10px] ${getLoadStatusClasses(detailLoad.status)}`}>
                    {detailLoad.status?.replace('_', ' ').toUpperCase()}
                  </Badge>
                  {detailInvoice?.invoice_number && (
                    <Badge className={`text-[10px] border flex items-center gap-1 ${INVOICE_STATUS_COLORS[detailInvoice.status] || INVOICE_STATUS_COLORS.draft}`}>
                      <FileText className="w-3 h-3" />
                      Invoice: {detailInvoice.invoice_number}
                    </Badge>
                  )}
                  {invoiceLoading && (
                    <span className="text-[10px] text-muted-foreground">Loading invoice...</span>
                  )}
                </div>
              </div>
              <div className="bg-surface rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <div>
                    {detailLoad.pickup_name && <span className="font-medium">{detailLoad.pickup_name} — </span>}
                    <span className="text-muted-foreground">{detailLoad.pickup_address}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  <div>
                    {detailLoad.delivery_name && <span className="font-medium">{detailLoad.delivery_name} — </span>}
                    <span className="text-muted-foreground">{detailLoad.delivery_address}</span>
                  </div>
                </div>
              </div>
              <div className="h-[200px] rounded-xl overflow-hidden border border-border">
                <MapView markers={[
                  { id: 'p', lat: detailLoad.pickup_lat, lng: detailLoad.pickup_lng, label: 'Pickup', type: 'pickup' },
                  { id: 'd', lat: detailLoad.delivery_lat, lng: detailLoad.delivery_lng, label: 'Delivery', type: 'delivery' },
                ]} loads={[detailLoad]} dark={false} zoom={6} />
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-surface rounded-xl p-3">
                  <p className="text-xs text-muted-foreground">Earned</p>
                  <p className="font-bold text-lg text-emerald-600">${detailLoad.rate?.toLocaleString()}</p>
                </div>
                <div className="bg-surface rounded-xl p-3">
                  <p className="text-xs text-muted-foreground">Distance</p>
                  <p className="font-bold text-lg">{milesToKm(detailLoad.distance_miles)} km</p>
                </div>
                <div className="bg-surface rounded-xl p-3">
                  <p className="text-xs text-muted-foreground">Rate/km</p>
                  <p className="font-bold text-lg">${ratePerKm(detailLoad.rate, detailLoad.distance_miles)}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm text-muted-foreground">
                <div><span className="text-muted-foreground">Weight:</span> {detailLoad.weight_lbs?.toLocaleString()} lbs</div>
                <div><span className="text-muted-foreground">Created:</span> {new Date(detailLoad.created_at).toLocaleDateString()}</div>
              </div>
              {detailLoad.status_history && (
                <div className="border-t border-border pt-3">
                  <p className="text-xs font-bold text-muted-foreground tracking-wider mb-2">STATUS HISTORY</p>
                  <div className="space-y-1">
                    {detailLoad.status_history.map((h, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <Badge className={`text-[10px] ${getLoadStatusClasses(h.status)}`}>
                          {h.status?.replace('_', ' ').toUpperCase()}
                        </Badge>
                        <span className="text-muted-foreground">{new Date(h.timestamp).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
